import '@assets/styles/tiptap-preview.css';

import { handleApiResponse } from '@src/api/apiClient/axiosClient';
import {
  ChannelMember,
  channelMemberApiClient,
} from '@src/api/shared/community/channelMemberApiClient';
import {
  Chat,
  chatApiClient,
  ChatGroup,
  LinkPreviewResponse,
  PinnedChat,
  ThreadChat,
} from '@src/api/shared/community/chatApiClient';
import { useSharedAuth } from '@src/context/auth/useSharedAuth';
import { useChatEditing } from '@src/context/ChatEditingContext';
import { ChannelMemberUserTypeEnum } from '@src/enum/ChannelMemberUserTypeEnum';
import { TENANT_ROLE } from '@src/enum/tenantRoleEnum';
import FileUploadOverlay from '@src/features/shared/community/components/chat/FileUploadOverlay';
import ReportModal from '@src/features/shared/community/components/chat/ReportModal';
import { ConfirmHidePreviewModal } from '@src/features/shared/community/components/ConfirmHidePreviewModal';
import { ConfirmMessageDeleteModal } from '@src/features/shared/community/components/ConfirmMessageDeleteModal';
import {
  SCROLL_CONFIG,
  TIMING_CONFIG,
} from '@src/features/shared/community/constants/chatBoxConfig';
import { useHideLinkPreview } from '@src/features/shared/community/hooks/useHideLinkPreview';
import { useThreadHandlers } from '@src/features/shared/community/hooks/useThreadHandlers';
import { useToast } from '@src/features/toast/context/toastContext';
import { useChannel } from '@src/hooks/useChannel';
import { useChatDelete } from '@src/hooks/useChatDelete';
import { useChatScrollManager } from '@src/hooks/useChatScrollManager';
import { useDebouncedEffect } from '@src/hooks/useDebouncedEffect';
import { useIOSKeyboard } from '@src/hooks/useIOSKeyboard';
import { useIsMobile } from '@src/hooks/useIsMobile';
import { ScrollPriority } from '@src/hooks/useScrollActionManager';
import {
  Attachment,
  deleteChatDraft,
  loadChatDraft,
  loadThreadDraft,
  saveChatDraft,
  saveThreadDraft,
} from '@src/idb';
import { resetChannelCounts } from '@src/store/channelSlice';
import { AppDispatch, RootState } from '@src/store/store.ts';
import { TiptapEditorHandle } from '@src/ui/Editor';
import { CommunityTiptapEditor } from '@src/ui/templates/Shared/CommunityTiptapEditor/CommunityTiptapEditor';
import { isTenantMember } from '@src/utils/checkTenantMemberPermission';
import { hasFiles } from '@src/utils/dragDropUtils';
import { ERROR_MESSAGES } from '@src/utils/errorMessages.ts';
import logger from '@src/utils/logger';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { ThreadProvider } from '../context/ThreadContext';
import { AnonymousPost } from '../enums/anonymousPost';
import { FileAttachment } from '../enums/fileAttachment';
import { useChatReport } from '../hooks/useChatReport';
import { ChatCard } from './chat/ChatCard';
import { ChatDateLabel } from './chat/chatList/ChatDateLabel';
import { ChatLoadingIndicator } from './chat/chatList/ChatLoadingIndicator';
import { ChatMenuPermission } from './chat/ChatMessage';
import LoginPrompt from './LoginPrompt';
import { ThreadPanel } from './thread';

type ChatBoxProps = {
  isLoggedIn: boolean;
  chatGroups: ChatGroup[];
  fetchMoreChats: () => Promise<void>;
  fetchMoreNewChats?: () => Promise<void>;
  fetchMoreNewerChats?: () => Promise<void>; // for infinite scroll down
  hasMoreChats: boolean;
  hasMoreNewChats?: boolean;
  hasMoreNewerChats?: boolean; // for infinite scroll down
  pinnedChat?: PinnedChat;
  onPinClick: (chat_id: number | undefined) => Promise<void>;
  onSend: () => void;
  mutateChats: () => void;
  contentHeight?: string;
  footerRef?: React.RefObject<HTMLDivElement>;
  onFooterHeightChange?: () => void;
  unreadCount?: number;
  firstUnreadChatId?: number | null;
  hasUnread?: boolean;
  initialChatId?: number;
  initialThreadId?: number;
  initialThreadHasMention?: boolean;
  setInitialChatId?: (chatId?: number) => void;
  renderHeader?: () => React.ReactNode; // Custom header slot
};
export type ChatBoxHandle = {
  scrollToChatById: (chatId: number) => void;
  scrollToUnreadOrBottom: (firstUnreadChatId?: number | null) => void;
  editPinnedChat: (pinnedChat: PinnedChat) => void;
  deletePinnedChat: (pinnedChat: PinnedChat) => void;
  reportPinnedChat: (pinnedChat: PinnedChat) => void;
  getChatMenuPermission: (chat: Chat) => ChatMenuPermission;
  openThreadByParentId: (parentChatId: number, threadChatId?: number) => void;
};

// Helper functions for scroll calculations
const calculateScrollToUnreadPosition = (
  element: HTMLDivElement,
  container: HTMLDivElement,
): number => {
  const containerRect = container.getBoundingClientRect();
  const elementRect = element.getBoundingClientRect();
  const newLineOffset = SCROLL_CONFIG.NEW_LINE_OFFSET;
  return container.scrollTop + (elementRect.top - containerRect.top) - newLineOffset;
};

const cancelScrollActions = (
  scrollManager: ReturnType<typeof useChatScrollManager>['scrollManager'],
  firstUnreadChatId: number,
): void => {
  scrollManager.cancelAction(`initialChatUnreadScroll-${firstUnreadChatId}`);
  scrollManager.cancelAction(`initialChatScroll-${firstUnreadChatId}`);
};

const createScrollToUnreadAction = (
  element: HTMLDivElement,
  container: HTMLDivElement,
): (() => void) => {
  return () => {
    const targetScrollTop = calculateScrollToUnreadPosition(element, container);
    container.scrollTo({ top: Math.max(0, targetScrollTop), behavior: 'smooth' });
  };
};

/**
 * Remove tenant user mentions from HTML content
 * This prevents mentioning tenant members in new posts while preserving existing messages
 */
const removeTenantMentionsFromHtml = (html: string): string => {
  if (!html) {
    return html;
  }

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    const mentions = doc.querySelectorAll('a.mention[data-mention-id]');

    mentions.forEach((mention) => {
      const href = mention.getAttribute('href');

      // Check if it's a tenant mention (URL contains /tenant/)
      // But exclude tenant admins and content editors
      if (href && href.includes('/tenant/')) {
        // Remove tenant mentions in edit mode
        // The paste handler will have better role-based filtering with full context
        const textContent = mention.textContent || '';
        const textNode = doc.createTextNode(textContent);
        mention.replaceWith(textNode);
      }
    });

    return doc.body.innerHTML;
  } catch (error) {
    logger.error('Error removing tenant mentions:', error);
    return html;
  }
};

const ChatBox = forwardRef<ChatBoxHandle, ChatBoxProps>(
  (
    {
      isLoggedIn,
      chatGroups,
      fetchMoreChats,
      fetchMoreNewerChats,
      hasMoreChats,
      hasMoreNewerChats,
      pinnedChat,
      onPinClick,
      onSend,
      mutateChats,
      footerRef,
      onFooterHeightChange,
      firstUnreadChatId,
      initialChatId,
      initialThreadId,
      initialThreadHasMention,
      renderHeader,
    },
    ref,
  ) => {
    const { channel } = useChannel();
    const dispatch = useDispatch<AppDispatch>();

    const [channelMembers, setChannelMembers] = useState<ChannelMember[]>([]);
    const chatContainerRef = useRef<HTMLDivElement>(null);
    const tiptapEditorRef = useRef<TiptapEditorHandle>(null);
    const [creatingMessage, setCreatingMessage] = useState<string>('');
    const [creatingAttachments, setCreatingAttachments] = useState<Attachment[]>([]);
    const { editingChatId, setEditingChatId } = useChatEditing();
    const [editingMessage, setEditingMessage] = useState<string>('');
    const [editingAttachments, setEditingAttachments] = useState<Attachment[]>([]);
    const [isAnonymous, setIsAnonymous] = useState<boolean>(false);
    const [linkPreviewCache, setLinkPreviewCache] = useState<
      Record<string, LinkPreviewResponse | null>
    >({});
    const [isActionMenuSpOpen, setIsActionMenuSpOpen] = useState<boolean>(false);
    const [currentChatIdSp, setCurrentChatIdSp] = useState<number>();
    const isLoadingMoreChats = useRef<boolean>(false);
    const [showLoadingIndicator, setShowLoadingIndicator] = useState<boolean>(false);
    const isLoadingNewerChats = useRef<boolean>(false);
    const prevChatCountBeforeFetch = useRef<number>(0);
    const isInitialRender = useRef<boolean>(true);
    const prevChannelCode = useRef<string | undefined>();
    const chatRefs = useRef<Record<number, HTMLDivElement | null>>({});

    const allChats = useMemo(() => chatGroups.flatMap((g) => g.chats), [chatGroups]);

    const totalChatCount = useMemo(() => allChats.length, [allChats]);

    const newestChat = useMemo(() => allChats[allChats.length - 1], [allChats]);

    const { profile } = useSharedAuth();
    const currentUserType = profile?.isTenant
      ? ChannelMemberUserTypeEnum.TENANT_USER
      : ChannelMemberUserTypeEnum.COMPANY_USER;
    const currentUserId = profile?.isTenant
      ? profile?.tenantProfile?.user?.id
      : profile?.companyProfile?.user?.id;

    const isUserTenantMember = isTenantMember(profile);

    const hasUserScrolledUpRef = useRef<boolean>(false);

    const {
      isNearBottom,
      updateWasAtBottom,
      scrollToBottom,
      rememberScrollHeight,
      restoreScrollHeight,
      scrollManager,
    } = useChatScrollManager({ chatContainerRef, chatRefs, hasUserScrolledUpRef });
    const wasAtBottomRef = useRef<boolean>(true);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [searchParams] = useSearchParams();
    const [deletingChatId, setDeletingChatId] = useState<number | null>(null);
    const { deleteMessage, isDeleting } = useChatDelete(channel.code);
    const toast = useToast();
    const { isReportModalOpen, handleChatReportClick, handleReportSubmit, handleReportCancel } =
      useChatReport({ channel, toast });
    const {
      isHidePreviewModalOpen,
      handleToggleLinkPreview,
      handleConfirmHidePreview,
      handleCancelHidePreview,
    } = useHideLinkPreview({
      channelCode: channel.code,
      mutateChats,
      setLinkPreviewCache,
      allChats,
    });
    const [isDragging, setIsDragging] = useState(false);
    const dragCounter = useRef(0);
    const [, forceUpdate] = useReducer((x) => x + 1, 0);
    const prevScrollToId = useRef<number | null>(null);
    const hasResetCountsRef = useRef<boolean>(false);
    const isFileDownloading = useSelector(
      (state: RootState) => state.fileDownload.isFileDownloading,
    );
    const firstUnreadThreadChatIdState = useSelector(
      (state: RootState) => state.thread.firstUnreadThreadChatId,
    );

    const isInfiniteScrollDownRef = useRef<boolean>(false);
    const isSendingRef = useRef<boolean>(false);
    const shouldAutoScrollAfterFooter = useRef<boolean>(false);

    const [viewportHeight, setViewportHeight] = useState(
      window.visualViewport?.height ?? window.innerHeight,
    );

    // Thread state
    const [isThreadOpen, setIsThreadOpen] = useState<boolean>(false);
    const [activeThreadParentId, setActiveThreadParentId] = useState<number | null>(null);
    const prevActiveThreadParentIdRef = useRef<number | null>(null);
    const [parentChat, setParentChat] = useState<ThreadChat | undefined>(undefined);
    const [threadReplies, setThreadReplies] = useState<ThreadChat[]>([]);
    const [threadPanelWidth, setThreadPanelWidth] = useState<number>(400);
    const [threadEditingChatId, setThreadEditingChatId] = useState<number | null>(null);
    const [threadEditingMessage, setThreadEditingMessage] = useState<string>('');
    const [threadEditingAttachments, setThreadEditingAttachments] = useState<Attachment[]>([]);
    const [threadCreatingMessage, setThreadCreatingMessage] = useState<string>('');
    const [threadCreatingAttachments, setThreadCreatingAttachments] = useState<Attachment[]>([]);
    const [threadIsAnonymous, setThreadIsAnonymous] = useState<boolean>(false);
    const [initialThreadChatId, setInitialThreadChatId] = useState<number | undefined>(undefined);
    const [showThreadUnreadLines, setShowThreadUnreadLines] = useState<boolean>(true);
    const [firstUnreadThreadChatId, setFirstUnreadThreadChatId] = useState<number | null>(null);
    const [chatBoxWidth, setChatBoxWidth] = useState<number>(0);
    const chatBoxRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      const ref = chatBoxRef.current;
      if (!ref) {
        return;
      }

      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          setChatBoxWidth(entry.contentRect.width);
        }
      });

      resizeObserver.observe(ref);
      setChatBoxWidth(ref.offsetWidth);

      return () => {
        resizeObserver.disconnect();
      };
    }, []);

    // propsからスレッド状態を同期（メンションがある場合のみ自動で開く）
    useEffect(() => {
      if (initialThreadId && initialChatId && initialThreadHasMention) {
        setIsThreadOpen(true);
        setActiveThreadParentId(initialChatId);
        setInitialThreadChatId(initialThreadId);
      }
    }, [initialThreadId, initialChatId, initialThreadHasMention]);

    // スレッドのハンドリング
    const {
      handleOpenThread: handleOpenThreadBase,
      handleThreadChatDelete,
      handleEditThreadReply,
      handleCloseThread: handleCloseThreadBase,
    } = useThreadHandlers({
      channel,
      activeThreadParentId,
      parentChat,
      threadReplies,
      setParentChat,
      setThreadReplies,
      setThreadEditingChatId,
      setThreadEditingMessage,
      setThreadEditingAttachments,
      setThreadIsAnonymous,
      setActiveThreadParentId,
      setIsThreadOpen,
      setInitialThreadChatId,
      setFirstUnreadThreadChatId,
    });

    // スレッドの開閉処理(ラッパー)
    // いずれも下書きデータは保持
    const handleOpenThread = useCallback(
      async (chatId: number, scrollToChatId?: number) => {
        await handleOpenThreadBase(chatId, scrollToChatId);
      },
      [handleOpenThreadBase],
    );

    const handleCloseThread = useCallback(() => {
      handleCloseThreadBase();
    }, [handleCloseThreadBase]);

    useEffect(() => {
      const onResize = () => {
        setViewportHeight(window.visualViewport?.height ?? window.innerHeight);
      };
      // visualViewport 対応ブラウザ
      window.visualViewport?.addEventListener('resize', onResize);
      // フォールバック
      window.addEventListener('resize', onResize);
      return () => {
        window.visualViewport?.removeEventListener('resize', onResize);
        window.removeEventListener('resize', onResize);
      };
    }, []);

    const scrollTimeoutRef = useRef<NodeJS.Timeout>();
    const loadingTimeoutRef = useRef<NodeJS.Timeout>();
    const abortControllerRef = useRef<AbortController>();

    useImperativeHandle(ref, () => ({
      scrollToChatById: (chatId: number) => {
        const el = chatRefs.current[chatId];
        if (el) {
          scrollManager.addScrollAction(
            () => {
              el.scrollIntoView({ block: 'center' });
            },
            ScrollPriority.USER_MANUAL,
            {
              description: `scrollToChatById-${chatId}`,
              force: true,
            },
          );
        }
      },
      scrollToUnreadOrBottom: (firstUnreadChatId?: number | null) => {
        if (isLoadingMoreChats.current) {
          setTimeout(() => {
            if (isLoadingMoreChats.current) {
              return;
            }

            if (hasUserScrolledUpRef.current) {
              return;
            }

            hasUserScrolledUpRef.current = false;

            if (firstUnreadChatId) {
              cancelScrollActions(scrollManager, firstUnreadChatId);
            }

            scrollManager.addScrollAction(
              () => {
                if (!firstUnreadChatId) {
                  scrollToBottom();
                  return;
                }

                const el = chatRefs.current[firstUnreadChatId];
                if (!el || !chatContainerRef.current) {
                  return;
                }

                const scrollAction = createScrollToUnreadAction(el, chatContainerRef.current);
                scrollAction();
              },
              ScrollPriority.USER_MANUAL,
              {
                description: `externalScrollToUnread-${firstUnreadChatId || 'bottom'}`,
                force: true,
              },
            );
          }, TIMING_CONFIG.INFINITE_SCROLL_BUFFER);
          return;
        }

        if (hasUserScrolledUpRef.current) {
          return;
        }

        if (!firstUnreadChatId) {
          scrollToBottom();
          return;
        }

        const el = chatRefs.current[firstUnreadChatId];
        if (!el) {
          return;
        }

        hasUserScrolledUpRef.current = false;

        cancelScrollActions(scrollManager, firstUnreadChatId);

        scrollManager.addScrollAction(
          () => {
            const chatContainer = chatContainerRef.current;
            if (!chatContainer) {
              return;
            }

            const scrollAction = createScrollToUnreadAction(el, chatContainer);
            scrollAction();
          },
          ScrollPriority.USER_MANUAL,
          {
            description: `scrollToUnread-${firstUnreadChatId}`,
            force: true,
          },
        );
      },
      editPinnedChat: async (pinnedChat: PinnedChat) => {
        const chat = chatGroups
          .flatMap((group) => group.chats)
          .find((chat) => chat.id === pinnedChat.id);
        if (chat) {
          handleChatEditClick(chat);
        }

        // まず親投稿を取得
        const replyParentChat = chatGroups
          .flatMap((group) => group.chats)
          .find((chat) => chat.id === pinnedChat.parent_chat_id);
        if (replyParentChat) {
          // 親投稿をキーにして返信データを取得
          const res = await chatApiClient.threads(channel.code, replyParentChat.id);
          handleApiResponse(res, (data) => {
            const threadChat = data.threads.find((t) => t.id === pinnedChat.id);
            if (threadChat) {
              handleOpenThread(replyParentChat.id, pinnedChat.id);
              handleEditThreadReply(threadChat);
            }
          });
        }
      },
      deletePinnedChat: (pinnedChat: PinnedChat) => {
        const chat = chatGroups
          .flatMap((group) => group.chats)
          .find((chat) => chat.id === pinnedChat.id);
        if (chat) {
          handleChatDeleteClick(chat);
        }
      },
      reportPinnedChat: (pinnedChat: PinnedChat) => {
        handleChatReportClick(pinnedChat as Chat);
      },
      getChatMenuPermission: (chat: Chat) => getChatMenuPermission(chat),
      openThreadByParentId: (parentChatId: number, threadChatId?: number) => {
        const chat = chatGroups
          .flatMap((group) => group.chats)
          .find((chat) => chat.id === parentChatId);
        if (chat) {
          handleOpenThread(chat.id, threadChatId);
        }
      },
    }));

    useEffect(() => {
      if (onFooterHeightChange) {
        onFooterHeightChange();
      }

      if (shouldAutoScrollAfterFooter.current) {
        scrollManager.addScrollAction(
          () => {
            return new Promise<void>((resolve) => {
              const adjustScroll = () => {
                scrollToBottom();

                requestAnimationFrame(() => {
                  const el = chatContainerRef.current;
                  if (!el) {
                    shouldAutoScrollAfterFooter.current = false;
                    resolve();
                    return;
                  }

                  const currentDistance = el.scrollHeight - el.scrollTop - el.clientHeight;
                  if (currentDistance > SCROLL_CONFIG.FOOTER_ADJUSTMENT_THRESHOLD) {
                    adjustScroll();
                  } else {
                    shouldAutoScrollAfterFooter.current = false;
                    resolve();
                  }
                });
              };

              adjustScroll();
            });
          },
          ScrollPriority.SEND_AUTO_SCROLL,
          { description: 'footerHeightAdjustment' },
        );
      }
    }, [onFooterHeightChange, footerRef?.current?.clientHeight, scrollToBottom, scrollManager]);

    useEffect(() => {
      const fetchChannelMembers = async () => {
        if (isFileDownloading) {
          return;
        }
        if (!channel.code) {
          return;
        }
        const res = await channelMemberApiClient.index(channel.code, { page: 'all' });
        handleApiResponse(res, (data) => {
          const allMembers = data.data.members;

          const filteredMembers = allMembers.filter((member) => {
            if (member.user_type === ChannelMemberUserTypeEnum.COMPANY_USER) {
              return true;
            }

            if (member.user_type === ChannelMemberUserTypeEnum.TENANT_USER) {
              return (
                member.role?.code === TENANT_ROLE.ADMIN || member.role?.code === TENANT_ROLE.CONTENT
              );
            }

            return true;
          });

          setChannelMembers(filteredMembers);
        });
      };

      if (isLoggedIn && channel.code !== '' && channel.code !== null) {
        fetchChannelMembers();
      }

      setIsActionMenuSpOpen(false);
      setCurrentChatIdSp(undefined);
      forceUpdate();
      prevChannelCodeRef.current = channel.code;
    }, [channel.code, isLoggedIn, isFileDownloading]);

    // Reduxのポーリングデータから未読情報を同期
    useEffect(() => {
      if (isThreadOpen && firstUnreadThreadChatIdState !== null) {
        setFirstUnreadThreadChatId(firstUnreadThreadChatIdState);
      }
    }, [firstUnreadThreadChatIdState, isThreadOpen]);

    useEffect(() => {
      const loadDraft = () => {
        if (currentUserType && currentUserId) {
          loadChatDraft(channel.code, currentUserType, currentUserId).then((draft) => {
            if (draft) {
              setCreatingMessage(draft.message);
              setCreatingAttachments(draft.attachments ?? []);
            }
          });
        }
      };

      if (prevChannelCode.current !== undefined && prevChannelCode.current !== channel.code) {
        isInitialRender.current = true;
      }

      loadDraft();

      const onVisible = () => {
        if (document.visibilityState === 'visible') {
          loadDraft();
        }
      };

      document.addEventListener('visibilitychange', onVisible);
      prevChannelCode.current = channel.code;

      return () => {
        document.removeEventListener('visibilitychange', onVisible);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [channel.code]);

    useEffect(() => {
      if (!isThreadOpen || !activeThreadParentId) {
        setThreadCreatingMessage('');
        setThreadCreatingAttachments([]);
        prevActiveThreadParentIdRef.current = null;
        return;
      }

      // スレッドをスイッチング(スレッドを閉じる操作をせずに別のスレッドを開く)した場合は、入力内容をクリア
      if (prevActiveThreadParentIdRef.current !== activeThreadParentId) {
        setThreadCreatingMessage('');
        setThreadCreatingAttachments([]);
      }

      prevActiveThreadParentIdRef.current = activeThreadParentId;

      const loadDraft = () => {
        if (!currentUserType || !currentUserId || !activeThreadParentId) {
          return;
        }

        loadThreadDraft(channel.code, activeThreadParentId, currentUserType, currentUserId).then(
          (draft) => {
            if (!draft) {
              return;
            }
            setThreadCreatingMessage(draft.message);
            setThreadCreatingAttachments(draft.attachments ?? []);
          },
        );
      };

      loadDraft();

      const onVisible = () => {
        if (document.visibilityState === 'visible') {
          loadDraft();
        }
      };

      document.addEventListener('visibilitychange', onVisible);

      return () => {
        document.removeEventListener('visibilitychange', onVisible);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isThreadOpen, activeThreadParentId, channel.code]);

    useDebouncedEffect(
      () => {
        if (editingChatId !== null) {
          return;
        }
        saveChatDraft(
          channel.code,
          creatingMessage,
          creatingAttachments,
          currentUserType,
          currentUserId,
        );
      },
      [creatingMessage, creatingAttachments, channel.code],
      500,
    );

    useDebouncedEffect(
      () => {
        // スレッドが閉じられている、または編集中、または親投稿IDがない場合は保存しない
        if (!isThreadOpen || threadEditingChatId !== null || !activeThreadParentId) {
          return;
        }
        saveThreadDraft(
          channel.code,
          activeThreadParentId,
          threadCreatingMessage,
          threadCreatingAttachments,
          currentUserType,
          currentUserId,
        );
      },
      [
        isThreadOpen,
        threadCreatingMessage,
        threadCreatingAttachments,
        channel.code,
        activeThreadParentId,
      ],
      500,
    );

    useLayoutEffect(() => {
      const chatContainer = chatContainerRef.current;
      if (!chatContainer) {
        return;
      }

      const hasChats = chatGroups?.some((group) => group.chats && group.chats.length > 0) || false;

      const performScrollAction = () => {
        try {
          if (scrollManager.isScrollInProgress()) {
            const currentAction = scrollManager.getCurrentAction();
            if (currentAction && currentAction.priority <= ScrollPriority.INFINITE_RESTORE) {
              return;
            }
          }

          // Infinite scroll restoration takes highest precedence
          if (isLoadingMoreChats.current && hasChats) {
            const currentChatCount = allChats.length;
            const hasNewChats = currentChatCount > prevChatCountBeforeFetch.current;

            if (!hasNewChats) {
              return;
            }

            // Clear the safety timeout since we successfully got new chats
            if (loadingTimeoutRef.current) {
              clearTimeout(loadingTimeoutRef.current);
            }

            scrollManager.addScrollAction(
              () => {
                return new Promise<void>((resolve) => {
                  requestAnimationFrame(() => {
                    scrollManager.cancelAction(`initialChatUnreadScroll-${initialChatId || 'any'}`);
                    scrollManager.cancelAction(`externalScrollToUnread-${initialChatId || 'any'}`);
                    scrollManager.cancelAction(`initialChatScroll-${initialChatId || 'any'}`);

                    restoreScrollHeight();
                    requestAnimationFrame(() => {
                      isLoadingMoreChats.current = false;
                      setShowLoadingIndicator(false);
                      resolve();
                    });
                  });
                });
              },
              ScrollPriority.INFINITE_RESTORE,
              { description: 'infiniteScrollRestore' },
            );
            return;
          }

          // Initial render bottom scroll
          if (isInitialRender.current && hasChats && !initialChatId) {
            scrollManager.addScrollAction(
              () => {
                scrollToBottom();
                isInitialRender.current = false;
              },
              ScrollPriority.GENERAL_AUTO_SCROLL,
              { description: 'initialRenderBottomScroll' },
            );
            return;
          }

          // Initial render with specific chat
          if (isInitialRender.current && initialChatId) {
            const el = chatRefs.current[initialChatId];
            if (!el) {
              return;
            }

            if (hasUserScrolledUpRef.current) {
              return;
            }

            scrollManager.cancelAction(`initialChatUnreadScroll-${initialChatId}`);
            scrollManager.cancelAction(`externalScrollToUnread-${initialChatId}`);

            scrollManager.addScrollAction(
              () => {
                el.scrollIntoView({ block: 'center' });
                if (pinnedChat?.id !== initialChatId && firstUnreadChatId !== initialChatId) {
                  el.classList.add('chat-highlight');
                }
                isInitialRender.current = false;
              },
              ScrollPriority.UNREAD_NAVIGATION,
              { description: `initialChatScroll-${initialChatId}` },
            );
            return;
          }

          // Auto-scroll for sender's messages only
          const shouldAutoScroll =
            isNearBottom() &&
            hasChats &&
            !initialChatId &&
            !isInfiniteScrollDownRef.current &&
            !isSendingRef.current &&
            !isLoadingMoreChats.current &&
            !hasUserScrolledUpRef.current;

          if (!shouldAutoScroll) {
            return;
          }

          const isSenderMessage =
            newestChat &&
            newestChat.user_id === currentUserId &&
            newestChat.user_type === currentUserType;

          if (!isSenderMessage) {
            return;
          }

          scrollManager.addScrollAction(
            () => scrollToBottom(),
            ScrollPriority.GENERAL_AUTO_SCROLL,
            { description: 'senderMessageAutoScroll' },
          );
        } catch (error) {
          logger.error('Error in performScrollAction:', error);
        }
      };

      performScrollAction();
    }, [
      chatGroups,
      allChats.length,
      isNearBottom,
      scrollToBottom,
      restoreScrollHeight,
      initialChatId,
      pinnedChat?.id,
      firstUnreadChatId,
      currentUserId,
      currentUserType,
      newestChat,
      scrollManager,
    ]);

    useEffect(() => {
      if (
        !initialChatId ||
        initialChatId === prevScrollToId.current ||
        isInitialRender.current ||
        isLoadingMoreChats.current
      ) {
        return;
      }

      const pinParam = searchParams.get('pin');
      const forceThreadDisplayParam = searchParams.get('force_thread_display');

      // el チェックや各種早期 return より前に dispatch し、確実にバッジをクリアする
      // hasResetCountsRef で channel 更新による再実行時の二重クリアを防ぐ
      if (
        (forceThreadDisplayParam === 'true' || pinParam === 'true') &&
        channel &&
        !hasResetCountsRef.current
      ) {
        dispatch(resetChannelCounts(channel.code));
        hasResetCountsRef.current = true;
      }

      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      const el = chatRefs.current[initialChatId];
      if (!el) {
        return;
      }

      // First unread chat scroll with infinite scroll guard - route through scroll manager
      if (firstUnreadChatId === initialChatId) {
        scrollTimeoutRef.current = setTimeout(() => {
          // Don't execute if scroll action in progress or infinite scroll started
          if (scrollManager.isScrollInProgress() || isLoadingMoreChats.current) {
            return;
          }

          if (hasUserScrolledUpRef.current) {
            return;
          }

          cancelScrollActions(scrollManager, initialChatId);

          scrollManager.addScrollAction(
            () => {
              const chatContainer = chatContainerRef.current;
              if (!chatContainer) {
                return;
              }

              const scrollAction = createScrollToUnreadAction(el, chatContainer);
              scrollAction();

              prevScrollToId.current = initialChatId;
              if (pinnedChat?.id !== initialChatId && firstUnreadChatId !== initialChatId) {
                el.classList.add('chat-highlight');
              }
            },
            ScrollPriority.UNREAD_NAVIGATION,
            { description: `initialChatUnreadScroll-${initialChatId}` },
          );
        }, SCROLL_CONFIG.DELAY_MS);
        return;
      }

      if (isLoadingMoreChats.current) {
        return;
      }

      if (hasUserScrolledUpRef.current) {
        return;
      }

      cancelScrollActions(scrollManager, initialChatId);

      scrollManager.addScrollAction(
        () => {
          el.scrollIntoView({ block: 'center' });
          prevScrollToId.current = initialChatId;
          if (pinnedChat?.id !== initialChatId && firstUnreadChatId !== initialChatId) {
            el.classList.add('chat-highlight');
          }
        },
        ScrollPriority.UNREAD_NAVIGATION,
        { description: `initialChatScroll-${initialChatId}` },
      );

      if (initialThreadId && (pinParam === 'true' || forceThreadDisplayParam === 'true')) {
        handleOpenThread(initialChatId, initialThreadId);
      }
    }, [
      initialChatId,
      chatGroups,
      firstUnreadChatId,
      pinnedChat,
      scrollManager,
      handleOpenThread,
      initialThreadId,
      searchParams,
      dispatch,
      channel,
    ]);

    useEffect(() => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const sortedChats = [...allChats].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      );

      abortControllerRef.current = new AbortController();
      const currentAbortController = abortControllerRef.current;

      const processChatPreview = (chat: Chat) => {
        if (currentAbortController.signal.aborted || isLoadingMoreChats.current) {
          return;
        }

        const key = String(chat.id);
        const newPreview = chat.is_link_preview_hidden ? null : (chat.preview ?? null);

        const cachedPreview = linkPreviewCache[key] ?? null;
        if (JSON.stringify(cachedPreview) === JSON.stringify(newPreview)) {
          return;
        }

        if (!currentAbortController.signal.aborted && !isLoadingMoreChats.current) {
          setLinkPreviewCache((prev) => ({ ...prev, [key]: newPreview }));
        }
      };

      const isChatVisible = (chatId: number) => {
        const chatElement = chatRefs.current[chatId];
        if (!chatElement || !chatContainerRef.current) {
          return false;
        }

        const container = chatContainerRef.current;
        const containerRect = container.getBoundingClientRect();
        const chatRect = chatElement.getBoundingClientRect();

        return chatRect.top >= containerRect.top && chatRect.bottom <= containerRect.bottom;
      };

      const processChats = () => {
        const visibleChats = sortedChats.filter((chat) => isChatVisible(chat.id));
        const nonVisibleChats = sortedChats.filter((chat) => !isChatVisible(chat.id));
        const prioritizedChats = [...visibleChats, ...nonVisibleChats];

        prioritizedChats.forEach((chat) => {
          if (!currentAbortController.signal.aborted) {
            processChatPreview(chat);
          }
        });
      };

      processChats();

      return () => {
        currentAbortController.abort();
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chatGroups, channel.code, allChats]);

    // スレッド返信のプレビューをlinkPreviewCacheに反映
    useEffect(() => {
      threadReplies.forEach((reply) => {
        const key = String(reply.id);
        const newPreview = reply.is_link_preview_hidden ? null : (reply.preview ?? null);
        const cachedPreview = linkPreviewCache[key] ?? null;
        if (JSON.stringify(cachedPreview) !== JSON.stringify(newPreview)) {
          setLinkPreviewCache((prev) => ({ ...prev, [key]: newPreview }));
        }
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [threadReplies]);

    const handleCreatingMessageChange = (text: string) => setCreatingMessage(text);
    const handleCreatingAttachmentsChange = (attachments: Attachment[]) => {
      setCreatingAttachments(attachments);
    };
    const handleEditingMessageChange = (text: string) => setEditingMessage(text);
    const handleEditingAttachmentsChange = (attachments: Attachment[]) => {
      setEditingAttachments(attachments);
    };

    const handleChatDeleteClick = useCallback((chat: Chat | ThreadChat) => {
      setDeletingChatId(chat.id);
      setIsDeleteModalOpen(true);
    }, []);

    const handleConfirmDelete = async () => {
      if (deletingChatId === null) {
        return;
      }

      // 返信なしの状態でスレッドビューを開いているときに、親投稿を削除した場合の処理
      const isDeletingActiveThreadParent = isThreadOpen && activeThreadParentId === deletingChatId;
      const hasActiveThreadReplies = threadReplies.some((reply) => !reply.is_trash_displayed);

      const success = await deleteMessage(deletingChatId);
      if (success) {
        if (isDeletingActiveThreadParent && !hasActiveThreadReplies) {
          handleCloseThread();
        }
        mutateChats();
      }
      setIsDeleteModalOpen(false);
      setDeletingChatId(null);
    };

    const handleCancelDelete = () => {
      tiptapEditorRef.current?.resetErrorMessage();
      setIsDeleteModalOpen(false);
      setDeletingChatId(null);
    };

    const [isSending, setIsSending] = useState(false);
    const [autoScrollOnNew, setAutoScrollOnNew] = useState(false);
    const sendCountRef = useRef(0);
    const pendingSendsRef = useRef<Set<number>>(new Set());
    const sendIdCounter = useRef(0);
    const sendPositionMap = useRef<Map<number, boolean>>(new Map());

    useEffect(() => {
      if (isSending && autoScrollOnNew) {
        shouldAutoScrollAfterFooter.current = true;

        const isMultipleSends = pendingSendsRef.current.size > 1;
        const scrollDelay = isMultipleSends
          ? TIMING_CONFIG.SEND_SCROLL_DELAY_MULTIPLE
          : TIMING_CONFIG.SEND_SCROLL_DELAY_SINGLE;

        scrollManager.addScrollAction(
          () => {
            return new Promise<void>((resolve) => {
              setTimeout(() => {
                scrollToBottom();

                // Verification with limited attempts
                let attempts = 0;
                const verifyAndCorrect = () => {
                  const el = chatContainerRef.current;
                  if (!el || attempts >= SCROLL_CONFIG.MAX_VERIFICATION_ATTEMPTS) {
                    resolve();
                    return;
                  }

                  const currentDistance = el.scrollHeight - el.scrollTop - el.clientHeight;
                  if (currentDistance <= SCROLL_CONFIG.SCROLL_VERIFICATION_THRESHOLD) {
                    resolve();
                    return;
                  }

                  attempts++;
                  scrollToBottom();
                  requestAnimationFrame(verifyAndCorrect);
                };

                requestAnimationFrame(verifyAndCorrect);
              }, scrollDelay);
            });
          },
          ScrollPriority.SEND_AUTO_SCROLL,
          { description: `sendAutoScroll-${sendCountRef.current}` },
        );

        setAutoScrollOnNew(false);
        setIsSending(false);

        sendCountRef.current = Math.max(0, sendCountRef.current - 1);

        if (pendingSendsRef.current.size === 0) {
          isSendingRef.current = false;
        }
      } else if (isSending && !autoScrollOnNew) {
        setIsSending(false);

        sendCountRef.current = Math.max(0, sendCountRef.current - 1);

        if (pendingSendsRef.current.size === 0) {
          isSendingRef.current = false;
        }
      }
    }, [chatGroups, isSending, autoScrollOnNew, scrollToBottom, scrollManager]);

    const handleSendClick = async () => {
      const userAtBottom = isNearBottom();
      sendCountRef.current += 1;
      sendIdCounter.current += 1;
      const currentSendId = sendIdCounter.current;

      pendingSendsRef.current.add(currentSendId);
      sendPositionMap.current.set(currentSendId, userAtBottom);
      isSendingRef.current = true;

      const shouldAutoScroll = sendPositionMap.current.get(currentSendId) || false;
      setAutoScrollOnNew(shouldAutoScroll);

      try {
        if (channel.code && currentUserType != null && currentUserId != null) {
          await deleteChatDraft(channel.code, currentUserType, currentUserId);
        }

        if (editingChatId !== null) {
          setLinkPreviewCache((prev) => {
            const newCache = { ...prev };
            delete newCache[String(editingChatId)];
            return newCache;
          });
          setEditingChatId(null);
          setEditingMessage('');
          setEditingAttachments([]);
        }

        setCreatingMessage('');
        setCreatingAttachments([]);
        setIsSending(true);

        await onSend?.();
      } catch (error) {
        logger.error('Error sending chat:', error);
        setIsSending(false);
        setAutoScrollOnNew(false);
        sendCountRef.current = Math.max(0, sendCountRef.current - 1);

        if (pendingSendsRef.current.size <= 1) {
          isSendingRef.current = false;
        }
      } finally {
        pendingSendsRef.current.delete(currentSendId);
        sendPositionMap.current.delete(currentSendId);

        if (pendingSendsRef.current.size === 0) {
          isSendingRef.current = false;
        }
      }
    };

    const handleChatEditClick = useCallback(
      (chat: Chat | ThreadChat) => {
        tiptapEditorRef.current?.resetErrorMessage();
        setEditingChatId(chat.id);
        // Sanitize tenant mentions when loading message for editing
        const sanitizedMessage = removeTenantMentionsFromHtml(chat.message);
        setEditingMessage(sanitizedMessage);
        setEditingAttachments(
          (chat.attachments ?? []).map((chatAttachment) => ({
            name: chatAttachment.file_name,
            type: chatAttachment.extension,
            size: 0,
            blob: new Blob(),
            chatAttachment: chatAttachment,
          })),
        );
        setIsAnonymous(chat.is_anonymous);
      },
      [setEditingChatId],
    );

    const handleScroll = useCallback(
      (event: Event) => {
        try {
          if (
            event.target !== chatContainerRef.current ||
            isLoadingMoreChats.current ||
            isLoadingNewerChats.current ||
            scrollManager.isScrollInProgress()
          ) {
            return;
          }

          const atBottom = isNearBottom();
          wasAtBottomRef.current = atBottom;

          // Reset infinite scroll flag if user scrolls back to bottom
          if (atBottom && hasUserScrolledUpRef.current) {
            hasUserScrolledUpRef.current = false;
          }

          const mentionPopup = document.querySelector('div[style*="z-index: 99999"]');
          if (mentionPopup) {
            return;
          }

          updateWasAtBottom();

          const chatContainer = chatContainerRef.current;
          if (!chatContainer) {
            return;
          }

          const { scrollTop, scrollHeight, clientHeight } = chatContainer;

          // Handle infinite scroll up (top)
          const shouldTriggerInfiniteScrollUp =
            chatContainer.scrollTop === 0 && hasMoreChats && !isLoadingMoreChats.current;

          if (shouldTriggerInfiniteScrollUp) {
            isLoadingMoreChats.current = true;
            setShowLoadingIndicator(true);
            const currentChatCount = allChats.length;
            prevChatCountBeforeFetch.current = currentChatCount;
            hasUserScrolledUpRef.current = true; // Mark that user has scrolled up for infinite scroll
            rememberScrollHeight();
            forceUpdate();

            // Safety timeout to reset loading state if fetch takes too long
            if (loadingTimeoutRef.current) {
              clearTimeout(loadingTimeoutRef.current);
            }
            loadingTimeoutRef.current = setTimeout(() => {
              if (isLoadingMoreChats.current) {
                isLoadingMoreChats.current = false;
                setShowLoadingIndicator(false);
              }
            }, TIMING_CONFIG.SAFETY_TIMEOUT);

            void fetchMoreChats();
            return;
          }

          // Handle infinite scroll down (bottom)
          const isVeryNearBottom =
            scrollTop + clientHeight >= scrollHeight - SCROLL_CONFIG.NEAR_BOTTOM_THRESHOLD;

          const shouldTriggerInfiniteScrollDown =
            isVeryNearBottom &&
            fetchMoreNewerChats &&
            hasMoreNewerChats &&
            !isLoadingNewerChats.current;

          if (!shouldTriggerInfiniteScrollDown) {
            return;
          }

          isLoadingNewerChats.current = true;
          isInfiniteScrollDownRef.current = true;
          hasUserScrolledUpRef.current = true;
          forceUpdate();

          rememberScrollHeight();

          void fetchMoreNewerChats()
            .then(() => {
              if (chatContainer) {
                wasAtBottomRef.current = false;
                restoreScrollHeight();
              }
            })
            .finally(() => {
              isLoadingNewerChats.current = false;
              setTimeout(() => {
                isInfiniteScrollDownRef.current = false;
              }, TIMING_CONFIG.INFINITE_SCROLL_DOWN_RESET);
              forceUpdate();
            });
        } catch (error) {
          logger.error('Error in handleScroll:', error);
        }
      },
      [
        fetchMoreChats,
        hasMoreChats,
        fetchMoreNewerChats,
        hasMoreNewerChats,
        isNearBottom,
        updateWasAtBottom,
        rememberScrollHeight,
        restoreScrollHeight,
        scrollManager,
        allChats,
      ],
    );

    const handlePinnedChatLongPress = useCallback(() => {
      tiptapEditorRef.current?.blur();
      setTimeout(() => {
        tiptapEditorRef.current?.blur();
        setIsActionMenuSpOpen(true);
      }, 0);
    }, []);

    const handlePinnedChatMenuClose = useCallback(() => {
      setIsActionMenuSpOpen(false);
    }, []);

    const handleDragEnter: React.DragEventHandler<HTMLDivElement> = (e) => {
      if (
        channel.allow_file_attachment !== FileAttachment.ENABLED ||
        !isLoggedIn ||
        !hasFiles(e.dataTransfer)
      ) {
        return;
      }
      e.preventDefault();
      dragCounter.current += 1;
      setIsDragging(true);
    };
    const handleDragLeave: React.DragEventHandler<HTMLDivElement> = (e) => {
      if (!isLoggedIn || channel.allow_file_attachment !== FileAttachment.ENABLED) {
        return;
      }
      e.preventDefault();

      if (dragCounter.current > 0) {
        dragCounter.current -= 1;
      }

      if (dragCounter.current === 0) {
        setIsDragging(false);
      }
    };
    const handleDragOver: React.DragEventHandler<HTMLDivElement> = (e) => {
      e.preventDefault();
    };
    const handleDropOnContainer: React.DragEventHandler<HTMLDivElement> = (e) => {
      if (isUserTenantMember) {
        return;
      }

      e.preventDefault();
      dragCounter.current = 0;
      setIsDragging(false);

      if (!isLoggedIn || channel.allow_file_attachment !== FileAttachment.ENABLED) {
        tiptapEditorRef.current?.setAttachmentError(ERROR_MESSAGES.FILE.ATTACHMENT_NOT_ALLOWED);
        return;
      }

      const files = e.dataTransfer.files;
      if (files && files.length > 0) {
        tiptapEditorRef.current?.selectFiles(files);
      }
    };

    const [isMentionScrolling, setIsMentionScrolling] = useState(false);

    useEffect(() => {
      const handleMentionScrollStart = () => setIsMentionScrolling(true);
      const handleMentionScrollEnd = () => setIsMentionScrolling(false);

      document.addEventListener('mentionScrollStart', handleMentionScrollStart);
      document.addEventListener('mentionScrollEnd', handleMentionScrollEnd);

      return () => {
        document.removeEventListener('mentionScrollStart', handleMentionScrollStart);
        document.removeEventListener('mentionScrollEnd', handleMentionScrollEnd);
      };
    }, []);

    useEffect(() => {
      const chatContainer = chatContainerRef.current;
      if (!chatContainer) {
        return;
      }

      const handleTouch = (e: TouchEvent) => {
        const target = e.target as HTMLElement;
        const mentionPopup = document.querySelector('div[style*="z-index: 99999"]');

        if (!mentionPopup || !chatContainer.contains(target) || !mentionPopup.contains(target)) {
          return;
        }

        if (isMentionScrolling) {
          e.stopPropagation();
        }
      };

      let scrollTimeoutId: NodeJS.Timeout;
      let lastScrollTime = 0;
      const SCROLL_DEBOUNCE_MS = SCROLL_CONFIG.DEBOUNCE_MS;
      const SCROLL_THROTTLE_MS = SCROLL_CONFIG.THROTTLE_MS;

      const debouncedHandleScroll = (event: Event) => {
        clearTimeout(scrollTimeoutId);

        const now = Date.now();
        if (now - lastScrollTime < SCROLL_THROTTLE_MS) {
          scrollTimeoutId = setTimeout(() => handleScroll(event), SCROLL_DEBOUNCE_MS);
          return;
        }

        lastScrollTime = now;

        const chatContainer = chatContainerRef.current;
        if (!chatContainer || isLoadingMoreChats.current || isLoadingNewerChats.current) {
          scrollTimeoutId = setTimeout(() => handleScroll(event), SCROLL_DEBOUNCE_MS);
          return;
        }

        const { scrollTop, scrollHeight, clientHeight } = chatContainer;

        // Top scroll - immediate infinite scroll trigger
        if (scrollTop === 0 && hasMoreChats) {
          handleScroll(event);
          return;
        }

        // Bottom scroll - immediate infinite scroll trigger
        const isVeryNearBottom =
          scrollTop + clientHeight >= scrollHeight - SCROLL_CONFIG.NEAR_BOTTOM_THRESHOLD;
        if (isVeryNearBottom && fetchMoreNewerChats && hasMoreNewerChats) {
          handleScroll(event);
          return;
        }

        scrollTimeoutId = setTimeout(() => handleScroll(event), SCROLL_DEBOUNCE_MS);
      };

      const handleScrollEvent = (e: Event) => {
        if (e.target !== chatContainer) {
          return;
        }

        const mentionPopup = document.querySelector('div[style*="z-index: 99999"]');
        if (mentionPopup || isMentionScrolling) {
          return;
        }

        debouncedHandleScroll(e);
      };

      const interceptTouch = (e: TouchEvent) => {
        const mentionPopup = document.querySelector('div[style*="z-index: 99999"]');
        const target = e.target as HTMLElement;

        if (!mentionPopup || !isMentionScrolling) {
          return;
        }

        if (!chatContainer.contains(target) || mentionPopup.contains(target)) {
          return;
        }

        e.preventDefault();
        e.stopPropagation();
      };

      chatContainer.addEventListener('scroll', handleScrollEvent, { passive: true });
      chatContainer.addEventListener('touchmove', handleTouch, { passive: false });
      document.body.addEventListener('touchmove', interceptTouch, {
        passive: false,
        capture: true,
      });

      return () => {
        clearTimeout(scrollTimeoutId);
        if (loadingTimeoutRef.current) {
          clearTimeout(loadingTimeoutRef.current);
        }
        chatContainer.removeEventListener('scroll', handleScrollEvent);
        chatContainer.removeEventListener('touchmove', handleTouch);
        document.body.removeEventListener('touchmove', interceptTouch, true);
      };
    }, [handleScroll, isMentionScrolling, hasMoreChats, fetchMoreNewerChats, hasMoreNewerChats]);

    const getChatMenuPermission = useCallback(
      (chat: Chat | ThreadChat): ChatMenuPermission => {
        if (!isLoggedIn) {
          return { canShowKebabMenu: true, canEdit: false, canDelete: false, canReport: true };
        }

        const isTenantAdmin = profile?.isTenant && profile?.tenantProfile?.user?.isAdmin;
        const isMyPost = chat.user_id === currentUserId && chat.user_type === currentUserType;

        const canEdit =
          (isTenantAdmin && isMyPost) || (!isTenantAdmin && isMyPost && channel?.can_post);
        const canDelete = isTenantAdmin || (!isTenantAdmin && isMyPost && channel?.can_post);
        const canReport = !isMyPost;
        const canShowKebabMenu = canEdit || canDelete || canReport;

        return { canShowKebabMenu, canEdit, canDelete, canReport };
      },
      [isLoggedIn, profile, currentUserId, currentUserType, channel?.can_post],
    );

    const shouldShowUnreadLine = useCallback(
      (chatId: number) => {
        return chatId === firstUnreadChatId;
      },
      [firstUnreadChatId],
    );

    const shouldShowThreadUnreadLine = useCallback(
      (chatId: number) => {
        return chatId === firstUnreadThreadChatId && showThreadUnreadLines;
      },
      [firstUnreadThreadChatId, showThreadUnreadLines],
    );

    const prevChatCountRef = useRef<number>(totalChatCount);
    // Total chat count auto-scroll (for sender messages only)
    useEffect(() => {
      const shouldAutoScroll =
        totalChatCount > prevChatCountRef.current &&
        isNearBottom() &&
        !isInfiniteScrollDownRef.current &&
        !isLoadingMoreChats.current &&
        !hasUserScrolledUpRef.current;

      if (!shouldAutoScroll) {
        prevChatCountRef.current = totalChatCount;
        return;
      }

      const isSenderMessage =
        newestChat &&
        newestChat.user_id === currentUserId &&
        newestChat.user_type === currentUserType;

      if (!isSenderMessage) {
        prevChatCountRef.current = totalChatCount;
        return;
      }

      scrollManager.addScrollAction(() => scrollToBottom(), ScrollPriority.GENERAL_AUTO_SCROLL, {
        description: 'totalChatCountAutoScroll',
      });

      prevChatCountRef.current = totalChatCount;
    }, [
      totalChatCount,
      isNearBottom,
      scrollToBottom,
      currentUserId,
      currentUserType,
      newestChat,
      scrollManager,
    ]);

    const isMobile = useIsMobile();
    const isInitFocus = isMobile ? false : isLoggedIn; // スマホなら絶対False、PCならログイン済みならTrue、未ログインならFalse

    useIOSKeyboard();

    const [showUnreadLines, setShowUnreadLines] = useState(true);
    const prevChannelCodeRef = useRef<string>();

    useEffect(() => {
      if (prevChannelCodeRef.current && prevChannelCodeRef.current !== channel.code) {
        setShowUnreadLines(false);
        hasUserScrolledUpRef.current = false; // Reset flag when changing channels

        setTimeout(() => {
          const chatContainer = chatContainerRef.current;
          if (chatContainer) {
            chatContainer.scrollTop = 0;
          }
        }, TIMING_CONFIG.SCROLL_RESET_DELAY);

        setTimeout(() => {
          setShowUnreadLines(true);
        }, TIMING_CONFIG.UNREAD_LINE_RESET_DELAY);
      }

      prevChannelCodeRef.current = channel.code;
    }, [channel.code]);

    useEffect(() => {
      const handleVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
          setShowUnreadLines(false);
          setTimeout(() => {
            setShowUnreadLines(true);
          }, TIMING_CONFIG.VISIBILITY_RESET_DELAY);
        }
      };

      const handlePageShow = (event: PageTransitionEvent) => {
        if (event.persisted) {
          setShowUnreadLines(false);
          setTimeout(() => {
            setShowUnreadLines(true);
          }, TIMING_CONFIG.VISIBILITY_RESET_DELAY);
        }
      };

      const handleBeforeUnload = () => {
        setShowUnreadLines(false);
      };

      document.addEventListener('visibilitychange', handleVisibilityChange);
      window.addEventListener('pageshow', handlePageShow);
      window.addEventListener('beforeunload', handleBeforeUnload);

      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        window.removeEventListener('pageshow', handlePageShow);
        window.removeEventListener('beforeunload', handleBeforeUnload);
      };
    }, []);

    useEffect(() => {
      const chatContainer = chatContainerRef.current;
      if (!chatContainer) {
        return;
      }

      const handleScrollToUnread = () => {
        if (!firstUnreadChatId || !showUnreadLines) {
          return;
        }

        const unreadElement = chatRefs.current[firstUnreadChatId];
        if (!unreadElement) {
          return;
        }

        const containerRect = chatContainer.getBoundingClientRect();
        const unreadRect = unreadElement.getBoundingClientRect();

        if (unreadRect.top < containerRect.top + SCROLL_CONFIG.UNREAD_VISIBILITY_THRESHOLD) {
          setShowUnreadLines(false);
        }
      };

      chatContainer.addEventListener('scroll', handleScrollToUnread);
      return () => {
        chatContainer.removeEventListener('scroll', handleScrollToUnread);
      };
    }, [firstUnreadChatId, showUnreadLines]);

    // useEffect for global drag cleanup
    useEffect(() => {
      const handleGlobalDragEnd = () => {
        if (isDragging) {
          setIsDragging(false);
          dragCounter.current = 0;
        }
      };

      const handleGlobalDrop = () => {
        setIsDragging(false);
        dragCounter.current = 0;
      };

      const handleMouseLeave = () => {
        if (isDragging) {
          setTimeout(() => {
            setIsDragging(false);
            dragCounter.current = 0;
          }, TIMING_CONFIG.VISIBILITY_RESET_DELAY);
        }
      };

      document.addEventListener('dragend', handleGlobalDragEnd);
      document.addEventListener('drop', handleGlobalDrop);
      document.addEventListener('mouseleave', handleMouseLeave);
      window.addEventListener('blur', handleGlobalDragEnd);

      return () => {
        document.removeEventListener('dragend', handleGlobalDragEnd);
        document.removeEventListener('drop', handleGlobalDrop);
        document.removeEventListener('mouseleave', handleMouseLeave);
        window.removeEventListener('blur', handleGlobalDragEnd);
      };
    }, [isDragging]);

    const threadContextValue = useMemo(
      () => ({
        isThreadOpen,
        setIsThreadOpen,
        activeThreadParentId,
        setActiveThreadParentId,
        parentChat,
        setParentChat,
        threadReplies,
        setThreadReplies,
        threadPanelWidth,
        setThreadPanelWidth,
        initialThreadChatId,
        setInitialThreadChatId,
        threadEditingChatId,
        setThreadEditingChatId,
        threadEditingMessage,
        setThreadEditingMessage,
        threadEditingAttachments,
        setThreadEditingAttachments,
        threadCreatingMessage,
        setThreadCreatingMessage,
        threadCreatingAttachments,
        setThreadCreatingAttachments,
        threadIsAnonymous,
        setThreadIsAnonymous,
        channel, // Pass the entire channel object
        channelMembers,
        pinnedChat,
        linkPreviewCache,
        isLoggedIn,
        currentUserId,
        currentUserType,
        isActionMenuSpOpen,
        currentChatIdSp,
        viewportHeight,
        showThreadUnreadLines,
        setShowThreadUnreadLines,
        firstUnreadThreadChatId,
        onChatEditClick: handleEditThreadReply,
        onChatDeleteClick: handleThreadChatDelete,
        onChatReportClick: handleChatReportClick,
        onSetCurrentChatIdSp: setCurrentChatIdSp,
        onPinnedChatMenuClose: handlePinnedChatMenuClose,
        onPinnedChatLongPress: handlePinnedChatLongPress,
        onToggleLinkPreview: handleToggleLinkPreview,
        onPinClick,
        getChatMenuPermission,
        shouldShowUnreadLine: shouldShowThreadUnreadLine,
      }),
      [
        isThreadOpen,
        activeThreadParentId,
        parentChat,
        threadReplies,
        threadPanelWidth,
        initialThreadChatId,
        threadEditingChatId,
        threadEditingMessage,
        threadEditingAttachments,
        threadCreatingMessage,
        threadCreatingAttachments,
        threadIsAnonymous,
        channel, // Pass the entire channel object
        channelMembers,
        pinnedChat,
        linkPreviewCache,
        isLoggedIn,
        currentUserId,
        currentUserType,
        isActionMenuSpOpen,
        currentChatIdSp,
        viewportHeight,
        showThreadUnreadLines,
        firstUnreadThreadChatId,
        setIsThreadOpen,
        setActiveThreadParentId,
        setParentChat,
        setThreadReplies,
        setThreadPanelWidth,
        setInitialThreadChatId,
        setThreadEditingChatId,
        setThreadEditingMessage,
        setThreadEditingAttachments,
        setThreadCreatingMessage,
        setThreadCreatingAttachments,
        setThreadIsAnonymous,
        setShowThreadUnreadLines,
        handleEditThreadReply,
        handleThreadChatDelete,
        handleChatReportClick,
        setCurrentChatIdSp,
        handlePinnedChatMenuClose,
        handlePinnedChatLongPress,
        handleToggleLinkPreview,
        onPinClick,
        getChatMenuPermission,
        shouldShowThreadUnreadLine,
      ],
    );

    return (
      <div className='flex flex-1 flex-row overflow-hidden'>
        <div
          ref={chatBoxRef}
          className='flex flex-1 flex-col overflow-hidden'
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDropOnContainer}
        >
          {renderHeader && renderHeader()}

          <div
            ref={chatContainerRef}
            id='chat-container'
            className='flex-1 flex-col overflow-auto overscroll-y-none pt-2'
          >
            <ChatLoadingIndicator isLoading={showLoadingIndicator} />

            {chatGroups.map((group, index) => (
              <div
                key={group.date_label + index}
                className='py-4'
              >
                <div className='border-t border-solid border-t-gray-200'>
                  <ChatDateLabel date={group.date_label} />
                  <div className='pt-1.5'>
                    {group.chats.map(
                      (chat) =>
                        channel && (
                          <ChatCard
                            key={chat.id}
                            chat={chat}
                            pinnedChat={pinnedChat ?? null}
                            linkPreviewCache={linkPreviewCache}
                            isLoggedIn={isLoggedIn}
                            currentUserId={currentUserId}
                            currentUserType={currentUserType}
                            editingChatId={editingChatId}
                            isActionMenuSpOpen={isActionMenuSpOpen}
                            currentChatIdSp={currentChatIdSp}
                            isMobile={isMobile}
                            shouldShowUnreadLine={shouldShowUnreadLine}
                            onPinClick={(chat_id: number | undefined) => onPinClick?.(chat_id)}
                            onChatEditClick={handleChatEditClick}
                            onChatDeleteClick={handleChatDeleteClick}
                            onChatReportClick={handleChatReportClick}
                            onPinnedChatMenuClose={handlePinnedChatMenuClose}
                            onSetCurrentChatIdSp={setCurrentChatIdSp}
                            onPinnedChatLongPress={handlePinnedChatLongPress}
                            onToggleLinkPreview={handleToggleLinkPreview}
                            onOpenThread={handleOpenThread}
                            getChatMenuPermission={getChatMenuPermission}
                            chatRef={(el) => (chatRefs.current[chat.id] = el)}
                            chatBoxWidth={chatBoxWidth}
                          />
                        ),
                    )}
                  </div>
                </div>
              </div>
            ))}

            <ChatLoadingIndicator isLoading={isLoadingNewerChats.current} />
          </div>
          {channel.can_post && !isUserTenantMember && (
            <div
              ref={footerRef}
              className={`relative z-10 shrink-0 bg-white px-2 py-3 lg:px-4 lg:pb-5 lg:pt-3 lg:[&_.toolbar]:hidden`}
            >
              <div
                className='touch-pan-y overflow-y-auto overscroll-contain'
                style={{
                  maxHeight: `${viewportHeight * 0.5}px`,
                  WebkitOverflowScrolling: 'touch',
                }}
              >
                <CommunityTiptapEditor
                  ref={tiptapEditorRef}
                  value={editingChatId !== null ? editingMessage : creatingMessage}
                  onChange={
                    editingChatId !== null
                      ? handleEditingMessageChange
                      : handleCreatingMessageChange
                  }
                  onSend={handleSendClick}
                  attachments={editingChatId !== null ? editingAttachments : creatingAttachments}
                  onAttachmentChange={
                    editingChatId !== null
                      ? handleEditingAttachmentsChange
                      : handleCreatingAttachmentsChange
                  }
                  enabled={{
                    richText: true,
                    mentions: true,
                    images: true,
                    links: true,
                    tables: false,
                    iframes: false,
                    textStyles: true, // This enables bold, italic, strike
                    lists: true, // This enables bulletList, orderedList
                    codeBlocks: false,
                    quotes: true, // This enables blockquote
                    dividers: false,
                    history: true,
                    dragDrop: false,
                    fileUpload: false,
                    fileSave: false,
                    shortcut: {
                      underline: false,
                      strike: true,
                      heading: false,
                      textAlign: false,
                      blockquote: true,
                    },
                  }}
                  height='100%'
                  width='100%'
                  containerHeight='100%'
                  channelCode={channel.code}
                  channelName={channel.name}
                  channelMembers={channelMembers}
                  channelVisibility={channel.visibility}
                  isAnonymous={isAnonymous}
                  onChangeIsAnonymous={(isAnonymous: boolean) => {
                    setIsAnonymous(isAnonymous);
                  }}
                  isEditing={editingChatId !== null}
                  editedChatId={editingChatId ?? undefined}
                  allowFileAttachment={channel.allow_file_attachment === FileAttachment.ENABLED}
                  allowAnonymousPost={channel.allow_anonymous_post === AnonymousPost.ENABLED}
                  onCancelEdit={() => {
                    setEditingChatId(null);
                    setEditingMessage('');
                    setEditingAttachments([]);
                    setIsAnonymous(false);
                    tiptapEditorRef.current?.resetErrorMessage();
                  }}
                  currentUserId={currentUserId}
                  currentUserType={currentUserType}
                  isInitFocus={isInitFocus}
                  isLoggedIn={isLoggedIn}
                />
              </div>
              <LoginPrompt
                chatBoxWidth={chatBoxWidth}
                isDisplayed={!isLoggedIn}
              />
              {isDeleteModalOpen && (
                <ConfirmMessageDeleteModal
                  onConfirm={handleConfirmDelete}
                  onClose={handleCancelDelete}
                  isDeleting={isDeleting}
                />
              )}
              {isHidePreviewModalOpen && (
                <ConfirmHidePreviewModal
                  onConfirm={handleConfirmHidePreview}
                  onClose={handleCancelHidePreview}
                />
              )}
            </div>
          )}
          <FileUploadOverlay
            condition={isDragging && !!channel.can_post && !isUserTenantMember}
            text={`「${channel.name}」にファイルをアップロードする`}
            className='pointer-events-none fixed inset-0 z-[1100] flex items-center justify-center bg-black/30'
            context='main'
          />
          {isReportModalOpen && (
            <ReportModal
              isOpen={isReportModalOpen}
              onClose={handleReportCancel}
              onSubmit={handleReportSubmit}
            />
          )}
        </div>

        {/* Thread Panel */}
        {isThreadOpen && activeThreadParentId && (
          <ThreadProvider value={threadContextValue}>
            <ThreadPanel
              width={threadPanelWidth}
              onWidthChange={setThreadPanelWidth}
            />
          </ThreadProvider>
        )}
      </div>
    );
  },
);

export default ChatBox;
